import { useQuery } from '@apollo/client';
import { IonRouterLink, useIonPopover } from '@ionic/react';
import { useCallback, useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router';

import styles from '@components/AccountSelector.module.css';
import PrimaryButton from '@components/buttons/PrimaryButton';
import ClientAvatar from '@components/ClientAvatar';
import NewJourneyForm from '@components/forms/NewJourneyForm';
import Modal, { ModalElement } from '@components/Modal';
import Icon from '@components/text/Icon';
import MiniCopy from '@components/text/MiniCopy';
import useMyClient from '@hooks/useMyClient';
import useMyExpert from '@hooks/useMyExpert';
import useOpener from '@hooks/useOpener';
import {
  GetClientWithJourneysDocument,
  GetClientWithJourneysQuery,
  GetClientWithJourneysQueryVariables,
  JourneyCreateMutation
} from '@typing/Generated';
import { clickCurrentTarget } from '@utils/keyboardUtils';
import { splitLOName } from '@utils/modelUtils';
import { isIosNativeApp } from '@utils/platformUtils';
import { journeyRoute } from '@utils/routeUtils';

type AccountPopoverProps = {
  client: GetClientWithJourneysQuery['client'] | undefined;
  closeMenu: () => void;
  handleJourneyCreate: () => void;
  handleJourneyIdSelect: (id: string) => void;
  journeyId: string;
};

type Journey = NonNullable<NonNullable<JourneyCreateMutation['journeyCreate']>['journey']>;

const AccountPopover = ({
  client,
  closeMenu,
  handleJourneyCreate,
  handleJourneyIdSelect,
  journeyId
}: AccountPopoverProps) => {
  const myExpert = useMyExpert();
  const clientHasUnattachedCarePlanningPackage = useMemo(
    () => client?.eligiblePackageGrants.some(grant => grant.hasCarePlanning && !grant.carePlanningJourneyId),
    [client?.eligiblePackageGrants]
  );

  return (
    <div className={styles.popover}>
      <div className={styles.title}>
        <MiniCopy className={styles.nameSpacing} color="neutral500">
          <FormattedMessage id="pages.client.header.journey" />
        </MiniCopy>
      </div>
      {client?.roles.map(role => (
        <div
          className={styles.item}
          data-test="role"
          key={role.id}
          onClick={() => handleJourneyIdSelect(role.journey.id)}
          onKeyUp={clickCurrentTarget}
          tabIndex={0}
        >
          <div className={styles.journeyGroup}>
            <div className={styles.journeyInfo}>
              <div className={styles.avatarSpacing}>
                <ClientAvatar
                  clientId={null}
                  color="primary500"
                  journeyId={journeyId}
                  {...splitLOName(role.journey.careProfile.name)}
                  size={30}
                />
              </div>
              <div className={styles.wrapText}>
                <MiniCopy className={styles.nameSpacing} color="neutral500">
                  {role.journey.careProfile.name}
                </MiniCopy>
              </div>
            </div>
            {role.journey.id === journeyId && (
              <div className={styles.end}>
                <div className={styles.viewProfile}>
                  <PrimaryButton
                    color="primary300"
                    onClick={e => {
                      e.stopPropagation();
                      closeMenu();
                    }}
                    routerLink={journeyRoute({
                      journeyId: role.journey.id,
                      linkAfterJourney: '/care-profile',
                      myExpert
                    })}
                    size="small"
                    textKey="pages.client.account.viewProfile"
                  />
                </div>
                <Icon ariaLabelKey="accessibility.ariaLabels.checkmark" className={styles.check} icon="checkmark" />
              </div>
            )}
          </div>
        </div>
      ))}
      {client && (client.hasUnlimitedMinutes || client.timeBalance > 0 || clientHasUnattachedCarePlanningPackage) ? (
        <div
          className={styles.journeyGroup}
          data-test="add"
          onClick={handleJourneyCreate}
          onKeyUp={clickCurrentTarget}
          tabIndex={0}
        >
          <div className={styles.addJourney}>
            <Icon ariaLabelKey="accessibility.ariaLabels.add" className={styles.addIcon} icon="plus" />
            <MiniCopy className={styles.nameSpacing} color="neutral500">
              <FormattedMessage id="pages.client.account.addAJourney" />
            </MiniCopy>
          </div>
        </div>
      ) : (
        <>
          {myExpert ? (
            <div className={styles.zeroBalance}>
              <FormattedMessage id="pages.client.account.expertZeroBalanceNoNewJourney" />
            </div>
          ) : (
            <>
              <div className={styles.zeroBalance}>
                <FormattedMessage id="pages.client.account.zeroBalanceNoNewJourney" />
              </div>
              <div className={styles.zeroBalance}>
                <FormattedMessage id="pages.client.account.zeroBalanceCreateJourney" />
              </div>
              {!isIosNativeApp() && (
                <div className={styles.zeroBalanceButton}>
                  <PrimaryButton
                    color="teal"
                    routerLink={journeyRoute({
                      journeyId,
                      linkAfterJourney: '/account?purchaseHours=true',
                      myExpert,
                      preventScroll: false
                    })}
                    textKey="pages.client.account.goToMyAccount"
                  />
                </div>
              )}
            </>
          )}
        </>
      )}
      <div className={styles.accountSettings}>
        <IonRouterLink
          className={styles.fullWidth}
          onClick={closeMenu}
          routerLink={journeyRoute({ journeyId, linkAfterJourney: '/account', myExpert, preventScroll: false })}
        >
          <div className={styles.accountSetting}>
            <MiniCopy color="neutral500">
              <FormattedMessage id="pages.client.account.accountDetails" />
            </MiniCopy>
          </div>
        </IonRouterLink>
        <IonRouterLink className={styles.fullWidth} onClick={closeMenu} routerLink={'/sign-out'}>
          <div className={styles.accountSetting}>
            <MiniCopy color="neutral500">
              <FormattedMessage id="dictionary.signOut" />
            </MiniCopy>
          </div>
        </IonRouterLink>
      </div>
      <div className={styles.otherSettings}>
        <IonRouterLink
          className={styles.fullWidth}
          href="https://www.withgrayce.com/terms"
          onClick={closeMenu}
          rel="noreferrer"
          target="_blank"
        >
          <div className={styles.otherSetting}>
            <MiniCopy color="neutral500">
              <FormattedMessage id="pages.client.account.termsOfUse" />
            </MiniCopy>
          </div>
        </IonRouterLink>
        <IonRouterLink className={styles.fullWidth} onClick={closeMenu} routerLink="/cookie-policy">
          <div className={styles.otherSetting}>
            <MiniCopy color="neutral500">
              <FormattedMessage id="menus.client.cookiePolicy" />
            </MiniCopy>
          </div>
        </IonRouterLink>
        <IonRouterLink
          className={styles.fullWidth}
          href="mailto: support@withgrayce.com"
          onClick={closeMenu}
          rel="noreferrer"
          target="_blank"
        >
          <div className={styles.otherSetting}>
            <MiniCopy color="neutral500">
              <FormattedMessage id="pages.client.account.contact" />
            </MiniCopy>
          </div>
        </IonRouterLink>
        <IonRouterLink
          className={styles.fullWidth}
          href="https://www.withgrayce.com/faq"
          onClick={closeMenu}
          rel="noreferrer"
          target="_blank"
        >
          <div className={styles.otherSetting}>
            <MiniCopy color="neutral500">
              <FormattedMessage id="pages.client.account.faq" />
            </MiniCopy>
          </div>
        </IonRouterLink>
      </div>
    </div>
  );
};

type Props = {
  clientId: string;
  journeyId: string;
};

const AccountSelector = ({ clientId, journeyId }: Props) => {
  const history = useHistory();
  const newJourneyModalOpener = useOpener();
  const newJourneyModalRef = useRef<ModalElement>(null);
  const myClient = useMyClient();
  const intl = useIntl();

  const getClientWithJourneys = useQuery<GetClientWithJourneysQuery, GetClientWithJourneysQueryVariables>(
    GetClientWithJourneysDocument,
    {
      variables: {
        id: clientId
      }
    }
  );

  const handleJourneyCreate = useCallback(() => {
    newJourneyModalOpener.open();
  }, [newJourneyModalOpener]);

  const handleNewJourneyError = useCallback(() => {
    if (newJourneyModalRef.current) {
      newJourneyModalRef.current.scrollToTop();
    }
  }, [newJourneyModalRef]);

  const handleJourneyIdSelect = useCallback(
    (value: string) => {
      history.push(journeyRoute({ journeyId: value, linkAfterJourney: '/care-plan', myClient }));
    },
    [history, myClient]
  );

  const [presentPopover, dismissPopover] = useIonPopover(
    <AccountPopover
      client={getClientWithJourneys.data?.client}
      closeMenu={() => dismissPopover()}
      handleJourneyCreate={handleJourneyCreate}
      handleJourneyIdSelect={id => {
        handleJourneyIdSelect(id);
        dismissPopover();
      }}
      journeyId={journeyId}
    />
  );

  const handleNewJourneyCancel = useCallback(() => {
    dismissPopover();
    newJourneyModalOpener.close();
  }, [dismissPopover, newJourneyModalOpener]);

  const handleJourneyCreated = useCallback(
    (journey: Journey) => {
      dismissPopover();
      newJourneyModalOpener.close();
      history.push(journeyRoute({ journeyId: journey.id, linkAfterJourney: '', myClient }));
    },
    [dismissPopover, history, myClient, newJourneyModalOpener]
  );

  return (
    <>
      <div className={styles.header}>
        <div
          className={styles.profileBox}
          onClick={e =>
            presentPopover({ cssClass: styles.popoverClass, event: e.nativeEvent, showBackdrop: false, size: 'auto' })
          }
        >
          {(myClient || getClientWithJourneys.data?.client) && (
            <div className={styles.avatarSpacing}>
              <ClientAvatar
                clientId={myClient?.id ?? (getClientWithJourneys.data?.client.id as string)}
                firstName={myClient?.firstName ?? (getClientWithJourneys.data?.client.firstName as string)}
                fontSize={13}
                journeyId={journeyId}
                lastName={myClient?.lastName ?? (getClientWithJourneys.data?.client.lastName as string)}
                size={30}
              />
            </div>
          )}
          <Icon ariaLabelKey="accessibility.ariaLabels.open" icon="chevronDown" />
        </div>
      </div>
      <Modal
        opener={newJourneyModalOpener}
        ref={newJourneyModalRef}
        title={intl.formatMessage({ id: 'pages.expert.journeys.newJourneyModal.title' })}
      >
        {({ modalDisplayed }) => (
          <>
            {getClientWithJourneys.data?.client && (
              <NewJourneyForm
                autofocus={modalDisplayed}
                clientId={getClientWithJourneys.data.client.id}
                onCancel={handleNewJourneyCancel}
                onError={handleNewJourneyError}
                onSuccess={handleJourneyCreated}
              />
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default AccountSelector;
